<template>
	<div class="participant-manager">
		<div class="header">
			<h3>参会医生名单</h3>
			<div class="actions">
				<el-button type="primary" size="small" icon="el-icon-plus" @click="showAddDialog">
					添加医生
				</el-button>
				<el-button type="success" size="small" icon="el-icon-upload2" @click="showImportDialog">
					批量导入
				</el-button>
			</div>
		</div>

		<!-- 参会人员表格 -->
		<el-table :data="participants" border stripe style="width: 100%" size="small"
			empty-text="暂无参会人员，请添加">
			<el-table-column type="index" label="序号" width="50" align="center" />
			<el-table-column prop="participantName" label="姓名" width="80">
				<template slot-scope="{ row }">
					<span>{{ row.participantName }}</span>
					<el-tag size="mini" type="warning" v-if="row.isNew" style="margin-left:4px">新</el-tag>
				</template>
			</el-table-column>
			<el-table-column prop="participantPhone" label="手机号" width="120">
				<template slot-scope="{ row }">
					{{ maskPhone(row.participantPhone) }}
				</template>
			</el-table-column>
			<el-table-column prop="hospitalName" label="医院" min-width="120">
				<template slot-scope="{ row }">{{ row.hospitalName || '-' }}</template>
			</el-table-column>
			<el-table-column prop="department" label="科室" width="90">
				<template slot-scope="{ row }">{{ row.department || '-' }}</template>
			</el-table-column>
			<el-table-column prop="title" label="职称" width="90">
				<template slot-scope="{ row }">{{ row.title || '-' }}</template>
			</el-table-column>
			<el-table-column prop="idCard" label="身份证号" width="145">
				<template slot-scope="{ row }">{{ maskIdCard(row.idCard) }}</template>
			</el-table-column>
			<el-table-column prop="bankCardNo" label="银行卡号" width="145">
				<template slot-scope="{ row }">{{ maskBankCard(row.bankCardNo) }}</template>
			</el-table-column>
			<el-table-column prop="role" label="参会角色" width="110">
				<template slot-scope="{ row }">
					<el-select v-model="row.role" size="mini" @change="onRoleChange(row)">
						<el-option label="讲者" value="SPEAKER" />
						<el-option label="主席" value="CHAIR" />
						<el-option label="会议主持" value="HOST" />
						<el-option label="学员" value="ATTENDEE" />
						<el-option label="其他" value="OTHER" />
					</el-select>
				</template>
			</el-table-column>
			<el-table-column label="认证状态" width="120" align="center">
				<template slot-scope="{ row }">
					<el-button
						v-if="!row.fadadaStatus || row.fadadaStatus === 'UNCERTIFIED'"
						size="mini" type="warning" plain
						@click="triggerCertify(row)">去认证</el-button>
					<el-tag v-else-if="row.fadadaStatus === 'PENDING'" size="mini" type="info">认证中</el-tag>
					<el-tag v-else-if="row.fadadaStatus === 'CERTIFIED'" size="mini" type="success">已通过</el-tag>
					<el-button
						v-else-if="row.fadadaStatus === 'FAILED'"
						size="mini" type="danger" plain
						@click="editParticipant(row, participants.indexOf(row))">修改重认证</el-button>
				</template>
			</el-table-column>
			<el-table-column label="操作" width="110" align="center">
				<template slot-scope="{ row, $index }">
					<el-button type="text" size="mini" icon="el-icon-edit"
						@click="editParticipant(row, $index)">编辑</el-button>
					<el-button type="text" size="mini" icon="el-icon-delete" style="color:#f56c6c"
						@click="removeParticipant(row, $index)">移除</el-button>
				</template>
			</el-table-column>
		</el-table>

		<div class="summary" v-if="participants.length">
			<span>共 <b>{{ participants.length }}</b> 人</span>
			<span v-for="(count, role) in roleSummary" :key="role" class="role-count">
				{{ roleLabel(role) }}: {{ count }}人
			</span>
		</div>

		<!-- 添加/编辑弹窗 -->
		<el-dialog :title="editingIndex >= 0 ? '编辑参会人员' : '添加参会人员'" :visible.sync="addDialogVisible"
			width="640px" :close-on-click-modal="false" append-to-body>

			<!-- 搜索已有医生 -->
			<div class="search-section" v-if="editingIndex < 0">
				<el-input v-model="searchKeyword" placeholder="搜索已有医生（姓名/手机号）..."
					prefix-icon="el-icon-search" size="small" clearable
					@input="debouncedSearch" style="margin-bottom: 12px" />
				<div class="search-results" v-if="searchResults.length">
					<div class="result-item" v-for="doc in searchResults" :key="doc.id"
						@click="selectDoctor(doc)" :class="{ disabled: isAlreadyAdded(doc) }">
						<div class="doc-info">
							<i class="el-icon-user-solid" style="color:#409eff; margin-right:6px" />
							<span class="name">{{ doc.fullName }}</span>
							<span class="phone">{{ maskPhone(doc.phoneNumber) }}</span>
							<span class="org" v-if="doc.hospitalName || doc.department">
								{{ doc.hospitalName }} {{ doc.department }}
							</span>
						</div>
						<el-tag size="mini" v-if="isAlreadyAdded(doc)" type="info">已添加</el-tag>
						<el-tag size="mini" v-else type="success">选择</el-tag>
					</div>
				</div>
				<div class="no-result" v-else-if="searchKeyword && !searching">
					<p>未找到匹配的医生</p>
					<el-button type="text" @click="switchToManual">
						<i class="el-icon-plus" /> 手动添加新人员
					</el-button>
				</div>
				<el-divider>或手动填写</el-divider>
			</div>

			<!-- 手动填写表单 -->
			<el-form :model="formData" :rules="formRules" ref="participantForm"
				label-width="80px" size="small">
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="姓名" prop="participantName">
							<el-input v-model="formData.participantName" placeholder="请输入姓名" />
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="手机号" prop="participantPhone">
							<el-input v-model="formData.participantPhone" placeholder="请输入手机号" maxlength="11" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="医院">
							<el-input v-model="formData.hospitalName" placeholder="医院/单位名称" />
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="科室">
							<el-input v-model="formData.department" placeholder="如：心内科" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="职称">
							<el-select v-model="formData.title" placeholder="选择职称" style="width:100%" clearable>
								<el-option label="主任医师" value="主任医师" />
								<el-option label="副主任医师" value="副主任医师" />
								<el-option label="主治医师" value="主治医师" />
								<el-option label="住院医师" value="住院医师" />
								<el-option label="教授" value="教授" />
								<el-option label="副教授" value="副教授" />
								<el-option label="其他" value="其他" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="参会角色">
							<el-select v-model="formData.role" placeholder="选择角色" style="width:100%">
								<el-option label="讲者" value="SPEAKER" />
								<el-option label="主席" value="CHAIR" />
								<el-option label="会议主持" value="HOST" />
								<el-option label="学员" value="ATTENDEE" />
								<el-option label="其他" value="OTHER" />
							</el-select>
						</el-form-item>
					</el-col>
				</el-row>
				<el-row :gutter="16">
					<el-col :span="24">
						<el-form-item label="开户行">
							<el-input v-model="formData.bankName" placeholder="如：中国建设银行北京朝阳支行" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-row :gutter="16">
					<el-col :span="8">
						<el-form-item label="税前劳务费">
							<el-input v-model="formData.laborFee" placeholder="元" type="number" />
						</el-form-item>
					</el-col>
					<el-col :span="8">
						<el-form-item label="税后金额">
							<el-input v-model="formData.laborFeeAfterTax" placeholder="元" type="number" />
						</el-form-item>
					</el-col>
					<el-col :span="8">
						<el-form-item label="税金">
							<el-input v-model="formData.taxAmount" placeholder="元" type="number" />
						</el-form-item>
					</el-col>
				</el-row>
				<el-row :gutter="16">
					<el-col :span="12">
						<el-form-item label="身份证号" prop="idCard">
							<el-input v-model="formData.idCard" placeholder="请输入18位身份证号" maxlength="18" />
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="银行卡号">
							<el-input v-model="formData.bankCardNo" placeholder="请输入银行卡号" />
						</el-form-item>
					</el-col>
				</el-row>
				<!-- 加入资料库选项（仅新建时显示） -->
				<el-row v-if="!formData.userId">
					<el-col :span="24">
						<el-form-item label=" " :label-width="'80px'">
							<el-checkbox v-model="formData.saveToDatabase">
								同时加入医生资料库（下次可直接搜索添加）
							</el-checkbox>
						</el-form-item>
					</el-col>
				</el-row>
			</el-form>

			<span slot="footer">
				<el-button size="small" @click="addDialogVisible = false">取消</el-button>
				<el-button type="primary" size="small" @click="confirmAdd">
					{{ editingIndex >= 0 ? '保存修改' : '确认添加医生' }}
				</el-button>
			</span>
		</el-dialog>

		<!-- 批量导入弹窗 -->
		<el-dialog title="批量导入参会人员" :visible.sync="importDialogVisible" width="500px" append-to-body>
			<el-upload drag :action="''" :auto-upload="false" :on-change="handleImportFile"
				:limit="1" accept=".xlsx,.xls,.csv">
				<i class="el-icon-upload"></i>
				<div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
				<div class="el-upload__tip" slot="tip">
					支持 Excel (.xlsx) 文件，需包含姓名、手机号列
					<el-link type="primary" :underline="false" @click="downloadTemplate">下载模板</el-link>
				</div>
			</el-upload>
		</el-dialog>
	</div>
</template>

<script>
import baseUrl from '@/request/config.js';
import axios from 'axios';

export default {
	name: 'ParticipantManager',
	props: {
		meetingId: { type: [Number, String], default: null },
		projectId: { type: [Number, String], default: null },
		value: { type: Array, default: () => [] },
		// 项目是否已配置电子签（true 则添加/导入后提示发起）
		contractConfigured: { type: Boolean, default: false }
	},
	data() {
		return {
			participants: [],
			addDialogVisible: false,
			editingIndex: -1,
			formData: this.emptyForm(),
			formRules: {
				participantName: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
				participantPhone: [
					{ required: true, message: '请输入手机号', trigger: 'blur' },
					{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
				],
				idCard: [
					{ pattern: /^\d{17}[\dXx]$/, message: '身份证号格式不正确', trigger: 'blur' }
				]
			},
			searchKeyword: '',
			searchResults: [],
			searching: false,
			searchTimer: null,
			importDialogVisible: false
		};
	},
	computed: {
		roleSummary() {
			const counts = {};
			this.participants.forEach(p => { counts[p.role] = (counts[p.role] || 0) + 1; });
			return counts;
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				// 如果是组件内部更新触发的回传，跳过，避免无限循环
				if (this._internalUpdate) return;
				if (val && val.length) this.participants = [...val];
			}
		},
		participants: {
			deep: true,
			handler(val) {
				this._internalUpdate = true;
				this.$emit('input', val);
				this.$emit('change', val);
				this.$nextTick(() => { this._internalUpdate = false; });
			}
		}
	},
	methods: {
		emptyForm() {
			return {
				participantName: '', participantPhone: '',
				hospitalName: '', department: '', title: '',
				idCard: '', bankCardNo: '', bankName: '',
				laborFee: '', laborFeeAfterTax: '', taxAmount: '',
				role: 'ATTENDEE', userId: null,
				isNew: false, saveToDatabase: true,
				fadadaStatus: 'UNCERTIFIED'
			};
		},

		// ===== 脱敏 =====
		maskPhone(val) {
			if (!val) return '-';
			return val.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
		},
		maskIdCard(val) {
			if (!val) return '-';
			return val.replace(/^(.{6})\d+(.{4})$/, '$1********$2');
		},
		maskBankCard(val) {
			if (!val) return '-';
			return val.replace(/^(.{4})\d+(.{4})$/, '$1 **** **** $2');
		},

		// ===== 搜索医生 =====
		debouncedSearch() {
			if (this.searchTimer) clearTimeout(this.searchTimer);
			this.searchTimer = setTimeout(() => this.searchDoctors(), 300);
		},
		async searchDoctors() {
			const kw = this.searchKeyword.trim();
			if (!kw) { this.searchResults = []; return; }
			this.searching = true;
			try {
				const token = JSON.parse(localStorage.getItem('token') || '{}');
				// 使用新后端接口搜索医生用户
				const res = await axios.get(`${baseUrl}/api/user/list`, {
					params: { userType: 'DOCTOR', keyword: kw, page: 1, size: 10 },
					headers: { token: token.token }
				});
				if (res.data.code === 200) {
					// 将 sys_user 字段映射为搜索结果需要的格式
					const records = res.data.data.records || [];
					this.searchResults = records.map(u => ({
						id: u.id,
						fullName: u.realName,
						phoneNumber: u.phone,
						hospitalName: u.hospitalName,
						department: u.department,
						title: u.title,
						idCard: u.idCard,
						bankCardNo: u.bankCardNo,
						bankName: u.bankName
					}));
				}
			} catch (e) {
				console.error('搜索医生失败:', e);
			} finally {
				this.searching = false;
			}
		},
		selectDoctor(doc) {
			if (this.isAlreadyAdded(doc)) return;
			this.formData = {
				...this.emptyForm(),
				participantName: doc.fullName || doc.realName || '',
				participantPhone: doc.phoneNumber || doc.phone || '',
				hospitalName: doc.hospitalName || doc.hospital || '',
				department: doc.department || '',
				title: doc.title || '',
				idCard: doc.idCard || '',
				bankCardNo: doc.bankCardNo || '',
				userId: doc.id,
				isNew: false
			};
		},
		isAlreadyAdded(doc) {
			return this.participants.some(
				p => p.participantPhone === (doc.phoneNumber || doc.phone) ||
					(p.userId && p.userId === doc.id)
			);
		},
		switchToManual() {
			this.formData = { ...this.emptyForm(), participantName: this.searchKeyword, isNew: true };
			this.searchKeyword = '';
			this.searchResults = [];
		},

		// ===== 添加/编辑 =====
		showAddDialog() {
			this.editingIndex = -1;
			this.formData = this.emptyForm();
			this.searchKeyword = '';
			this.searchResults = [];
			this.addDialogVisible = true;
		},
		editParticipant(row, index) {
			this.editingIndex = index;
			this.formData = { ...row };
			this.addDialogVisible = true;
		},
		confirmAdd() {
			this.$refs.participantForm.validate(async valid => {
				if (!valid) return;
				const data = { ...this.formData };
				data.inviteStatus = data.inviteStatus || 'PENDING';
				const isEdit = this.editingIndex >= 0;
				if (isEdit) {
					this.$set(this.participants, this.editingIndex, data);
					this.$message.success('已更新');
				} else {
					const dup = this.participants.find(p => p.participantPhone === data.participantPhone);
					if (dup) { this.$message.warning(`${data.participantName} 已在名单中`); return; }
					if (!data.userId) data.isNew = true;
					this.participants.push(data);
					this.$message.success(`已添加 ${data.participantName}`);
				}
				this.addDialogVisible = false;

				// 场景②：新增医生到已有会议 → 提示发起电子签
				if (!isEdit && this.meetingId && this.contractConfigured && data.laborFee) {
					try {
						await this.$confirm(
							`是否立即为 ${data.participantName} 发起电子签署？`,
							'发起签署',
							{ confirmButtonText: '立即发起', cancelButtonText: '稍后手动', type: 'info' }
						);
						// 先保存到服务端拿到 participantId，再发起
						await this.saveToServer(this.meetingId);
						await this.triggerSignForLatest();
					} catch (e) {
						// 点「稍后手动」走这里，不报错
					}
				}
			});
		},
		removeParticipant(row, index) {
			this.$confirm(`确定移除 ${row.participantName} 吗？`, '提示', { type: 'warning' })
				.then(() => {
					this.participants.splice(index, 1);
					this.$message.success('已移除');
				}).catch(() => {});
		},
		onRoleChange() {
			this.$emit('input', [...this.participants]);
		},

		// ===== 法大大认证 =====
		triggerCertify(row) {
			if (!row.idCard || !row.bankCardNo) {
				this.$message.warning('请先完善身份证号和银行卡号后再认证');
				this.editParticipant(row, this.participants.indexOf(row));
				return;
			}
			this.$confirm(`确认对 ${row.participantName} 发起法大大实名认证？`, '认证确认', {
				type: 'info', confirmButtonText: '确认认证', cancelButtonText: '取消'
			}).then(() => {
				// 更新为认证中状态
				const idx = this.participants.indexOf(row);
				this.$set(this.participants[idx], 'fadadaStatus', 'PENDING');
				this.$message.info('认证请求已发送，请稍候...');
				// TODO: 调用后端 /api/participant/certify 接口
				// axios.post(`${baseUrl}/api/participant/certify`, {
				//   participantId: row.id,
				//   name: row.participantName,
				//   idCard: row.idCard,
				//   bankCardNo: row.bankCardNo,
				//   phone: row.participantPhone
				// }).then(res => { ... })
			}).catch(() => {});
		},

		// ===== 批量导入 =====
		showImportDialog() { this.importDialogVisible = true; },
		async handleImportFile(file) {
			if (!this.meetingId && !this._tempMeetingId) {
				this.$message.warning('请先保存会议基本信息后再批量导入');
				return;
			}
			const token = JSON.parse(localStorage.getItem('token') || '{}');
			const formData = new FormData();
			formData.append('file', file.raw);
			formData.append('meetingId', this.meetingId || this._tempMeetingId);
			if (this.projectId) formData.append('orgId', this.projectId);
			try {
				const res = await axios.post(`${baseUrl}/api/participant/import-excel`, formData, {
					headers: { token: token.token, 'Content-Type': 'multipart/form-data' }
				});
				if (res.data.code === 200) {
					const { successCount, failCount, errors } = res.data.data;
					this.$message.success(`导入完成：成功 ${successCount} 条，失败 ${failCount} 条`);
					if (errors && errors.length) console.warn('导入错误详情:', errors);
					this.importDialogVisible = false;
					await this.loadFromServer();

					// 场景③：批量导入到已有会议 → 提示批量发起电子签
					const mid = this.meetingId || this._tempMeetingId;
					if (mid && this.contractConfigured && successCount > 0) {
						try {
							await this.$confirm(
								`已导入 ${successCount} 位医生，是否立即为他们发起电子签署？`,
								'批量发起签署',
								{ confirmButtonText: '立即发起', cancelButtonText: '稍后手动', type: 'info' }
							);
							await this.triggerSignForAll(mid);
						} catch (e) { /* 稍后手动 */ }
					}
				} else {
					this.$message.error(res.data.msg || '导入失败');
				}
			} catch (e) {
				this.$message.error('上传失败，请检查网络');
			}
		},
		downloadTemplate() {
			window.open('https://sign-new-test.oss-cn-beijing.aliyuncs.com/templates/doctor-import-template.xlsx');
		},

		// ===== 电子签触发辅助方法 =====
		/** 场景②：为最后一个新加的参会人发起签署（saveToServer 后调用） */
		async triggerSignForLatest() {
			const mid = this.meetingId;
			if (!mid) return;
			const token = JSON.parse(localStorage.getItem('token') || '{}');
			try {
				// 重新拉列表拿到服务端分配的 participantId
				const listRes = await axios.get(`${baseUrl}/api/participant/meeting/${mid}`, {
					headers: { token: token.token }
				});
				const list = listRes.data?.data || [];
				// 找最新一条未签署且有劳务费的参会人
				const target = [...list].reverse().find(p => p.laborFee && !['SIGNING','SIGNED'].includes(p.fadadaStatus));
				if (!target) { this.$message.warning('未找到可发起签署的参会人'); return; }
				const res = await axios.post(`${baseUrl}/api/contract/initiate-one/${target.id}`, {}, {
					headers: { token: token.token }
				});
				if (res.data?.code === 200) {
					this.$message.success(`✅ 已为 ${target.participantName} 发起签署`);
				} else {
					this.$message.warning('签署发起失败：' + (res.data?.msg || ''));
				}
			} catch (e) {
				this.$message.error('签署发起异常：' + e.message);
			}
		},

		/** 场景③：为会议所有未签署医生批量发起 */
		async triggerSignForAll(meetingId) {
			const mid = meetingId || this.meetingId;
			if (!mid) return;
			const token = JSON.parse(localStorage.getItem('token') || '{}');
			try {
				const res = await axios.post(`${baseUrl}/api/contract/initiate/${mid}`, {}, {
					headers: { token: token.token }
				});
				const d = res.data?.data || {};
				this.$notify({
					title: '签署发起完成',
					message: `成功 ${d.success || 0} 人${d.fail ? `，失败 ${d.fail} 人` : ''}`,
					type: d.fail ? 'warning' : 'success',
					position: 'bottom-right'
				});
			} catch (e) {
				this.$message.error('批量签署发起失败：' + e.message);
			}
		},

		// ===== 辅助 =====
		statusType(s) { return { PENDING: 'info', ACCEPTED: 'success', ATTENDED: 'success', DECLINED: 'danger' }[s] || 'info'; },
		statusText(s) { return { PENDING: '待确认', ACCEPTED: '已接受', ATTENDED: '已签到', DECLINED: '已拒绝' }[s] || '待确认'; },
		roleLabel(r) { return { SPEAKER: '讲者', CHAIR: '主席', HOST: '会议主持', ATTENDEE: '学员', OTHER: '其他' }[r] || r; },

		// ===== 服务端同步 =====
		async saveToServer(meetingId) {
			const mid = meetingId || this.meetingId;
			if (!mid || !this.participants.length) return;
			const token = JSON.parse(localStorage.getItem('token') || '{}');
			try {
				const payload = {
					meetingId: mid,
					participants: this.participants.map(p => ({
						meetingId: mid,
						userId: p.userId || null,
						participantName: p.participantName,
						participantPhone: p.participantPhone,
						hospitalName: p.hospitalName,
						department: p.department,
						title: p.title,
						idCard: p.idCard,
						bankCardNo: p.bankCardNo,
						bankName: p.bankName,
						role: p.role,
						laborFee: p.laborFee,
						laborFeeAfterTax: p.laborFeeAfterTax,
						taxAmount: p.taxAmount,
						fadadaStatus: p.fadadaStatus
					}))
				};
				const res = await axios.post(`${baseUrl}/api/participant/batch`, payload, {
					headers: { token: token.token }
				});
				if (res.data.code === 200) {
					console.log('[ParticipantManager] 参会医生已保存, meetingId=', mid);
				} else {
					this.$message.warning('参会医生保存失败: ' + (res.data.msg || ''));
				}
			} catch (e) {
				console.error('[ParticipantManager] saveToServer error:', e);
			}
		},
		async loadFromServer() {
			const mid = this.meetingId;
			if (!mid) return;
			const token = JSON.parse(localStorage.getItem('token') || '{}');
			try {
				const res = await axios.get(`${baseUrl}/api/participant/meeting/${mid}`, {
					headers: { token: token.token }
				});
				if (res.data.code === 200) {
					this.participants = res.data.data || [];
					console.log('[ParticipantManager] 已加载参会医生:', this.participants.length, '人');
				}
			} catch (e) {
				console.error('[ParticipantManager] loadFromServer error:', e);
			}
		},
	}
};
</script>

<style lang="scss" scoped>
.participant-manager {
	background: #fff;
	border-radius: 8px;
	padding: 16px;

	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 12px;

		h3 { font-size: 16px; margin: 0; color: #333; }
	}

	.summary {
		margin-top: 10px;
		font-size: 13px;
		color: #666;
		b { color: #409eff; }
		.role-count {
			margin-left: 16px;
			padding: 2px 8px;
			background: #f0f2f5;
			border-radius: 4px;
		}
	}
}

.search-section {
	.search-results {
		max-height: 200px;
		overflow-y: auto;
		border: 1px solid #ebeef5;
		border-radius: 4px;
		margin-bottom: 12px;

		.result-item {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 8px 12px;
			cursor: pointer;
			transition: background 0.2s;

			&:hover:not(.disabled) { background: #ecf5ff; }
			&.disabled { cursor: not-allowed; opacity: 0.6; }

			.doc-info {
				display: flex;
				align-items: center;
				gap: 8px;
				.name { font-weight: 500; color: #333; }
				.phone { color: #909399; font-size: 13px; }
				.org { color: #409eff; font-size: 12px; padding: 1px 6px; background: #ecf5ff; border-radius: 3px; }
			}
		}
	}

	.no-result {
		text-align: center;
		padding: 16px;
		color: #909399;
		p { margin-bottom: 8px; }
	}
}
</style>
